import React, {
    ReactElement,
    ReactNode,
    createContext,
    useMemo,
    useState,
} from "react"

import { AppError } from "../../../../../errors/errorProcessors"
import usePublishDevice from "../../../../../services/middleware/hooks/devices/ttr/usePublishDevice"
import useSaveDeviceDraft from "../../../../../services/middleware/hooks/devices/ttr/useSaveDeviceDraft"
import { TTypeTestVersionPostBody } from "../../../../../services/middleware/types/devices/ttr/publishDeviceTypes"
import { checkContextErrors } from "../../../../../utils/registerDevice/registerDeviceUtils"
import { IDeviceFormContextValue } from "../../../types/providerTypes"
import { TTRDeviceFormData } from "../../types/providerTypes"

type CreateDeviceProviderProps = {
    formData: TTRDeviceFormData
    setFormData: React.Dispatch<React.SetStateAction<TTRDeviceFormData>>
    formDataIsLoading: boolean
    formDataError?: AppError
    deviceId?: string
    deviceRef?: string
    attachmentUUID?: string
    supersededDeviceID?: number
    complianceComments?: string
    complianceStatusNotes?: string
    typeTestVersionPostBody?: TTypeTestVersionPostBody
    canEditManufacturer: boolean
    children: ReactNode
}

export const CreateDeviceContext = createContext(
    {} as IDeviceFormContextValue<TTRDeviceFormData>
)

const CreateDeviceProvider = ({
    formData,
    setFormData,
    formDataIsLoading,
    formDataError,
    deviceId,
    deviceRef,
    attachmentUUID,
    supersededDeviceID,
    complianceComments,
    complianceStatusNotes,
    canEditManufacturer,
    typeTestVersionPostBody,
    children,
}: CreateDeviceProviderProps): ReactElement => {
    const [currentIndex, setCurrentIndex] = useState<number>(0)
    const [errors, setErrors] = useState<AppError[]>([])

    const {
        publishDevice: publishNewVersion,
        error: publishError,
        isLoaded: publishSuccessful,
        isLoading: publishInProgress,
    } = usePublishDevice(formData, deviceId, deviceRef)

    const {
        saveDeviceDraft,
        isLoading: saveDraftInProgress,
        isLoaded: saveDraftSuccessful,
    } = useSaveDeviceDraft(
        deviceRef,
        deviceId,
        attachmentUUID,
        supersededDeviceID,
        complianceComments,
        complianceStatusNotes,
        typeTestVersionPostBody
    )

    checkContextErrors([formDataError, publishError], errors, (error) =>
        setErrors((prev) => [...prev, error])
    )
    const setIndex = (step: number): void => {
        setErrors([])
        setCurrentIndex(step)
    }

    const value: IDeviceFormContextValue<TTRDeviceFormData> = useMemo(
        () => ({
            formData,
            setFormData,
            currentIndex,
            setCurrentIndex: setIndex,
            deviceId,
            deviceRef,
            onSubmit: publishNewVersion,
            submitLoading: publishInProgress,
            submitSuccessful: publishSuccessful,
            canEditManufacturer,
            formIsLoading: formDataIsLoading,
            saveDraft: saveDeviceDraft,
            saveDraftLoading: saveDraftInProgress,
            saveDraftSuccessful,
            errors,
        }),
        [
            formData,
            setFormData,
            currentIndex,
            deviceId,
            deviceRef,
            publishNewVersion,
            publishInProgress,
            publishSuccessful,
            canEditManufacturer,
            formDataIsLoading,
            saveDeviceDraft,
            saveDraftInProgress,
            saveDraftSuccessful,
            errors,
        ]
    )

    return (
        <CreateDeviceContext.Provider value={value}>
            {children}
        </CreateDeviceContext.Provider>
    )
}

CreateDeviceProvider.defaultProps = {
    formDataError: undefined,
    deviceId: undefined,
    deviceRef: undefined,
    attachmentUUID: undefined,
    supersededDeviceID: undefined,
    complianceComments: undefined,
    complianceStatusNotes: undefined,
    typeTestVersionPostBody: undefined,
}

export default CreateDeviceProvider
